import { Turn } from "./Turn";
import {toJS} from "mobx";
import {observer, inject} from "mobx-react";
import { useAutoScroll } from "../hooks/useAutoScroll";
import { ROLE_SYSTEM, ROLE_USER } from "./LLMClient";
import { formatTimestamp } from "../lib/utils";
import { calculateCost } from "./modelPricing";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";

// Accumulate token usage across all turns
const accumulateUsage = (turns) => {
  return turns.filter(t => t != null).reduce((acc, turn) => {
    const { usage = {} } = turn.metadata || {};
    return {
      inputTokens: (acc.inputTokens || 0) + (usage.inputTokens || 0),
      outputTokens: (acc.outputTokens || 0) + (usage.outputTokens || 0),
      cacheReadInputTokens: (acc.cacheReadInputTokens || 0) + (usage.cacheReadInputTokens || 0),
      cacheWriteInputTokens: (acc.cacheWriteInputTokens || 0) + (usage.cacheWriteInputTokens || 0),
      totalTokens: (acc.totalTokens || 0) + (usage.totalTokens || 0)
    };
  }, {});
};

export const Conversation = observer(({
                               className = "",
                               turns,
                               trapErrors = true,
                               agentId = null,
                               welcome = null,
                               showAdvancedFeatures = false,
                               showTimestamps = true,
                               showDetails = false,
                               busy = false,
                               resolveSourceLinks = null,
                               replayConversation = null,
                               discardConversation = null,
                               children = null,
                               userStore = null,
                               editingTurnIndex = null,
                               onEditPrompt = null,
                               onSaveEdit = null,
                               onCancelEdit = null,
                               onVisibilityChange = null,
                               subAgentTurns = null,
                             }) => {
  const conversationRef = useRef(null);
  // Track genes mentioned across all turns in this conversation
  const mentionedGenesRef = useRef(new Set());

  // Use the first timestamp found as "start"
  const startTs = turns.filter(t => t != null).reduce((result, turn) => result || turn.ts, null) || Date.now();

  // toolUseLookup: memoized on turn count so text deltas (content_block_delta) don't
  // trigger O(N×blocks) rebuild on every event.
  const toolUseLookupBase = useMemo(() => {
    const lookup = turns
      .filter(turn => turn != null && turn.content)
      .reduce((result, turn) => {
        const forTurn = Object.fromEntries(
          turn.content
            .filter(b => b?.toolUse)
            .map(b => [b.toolUse.toolUseId, toJS(b)])
        );
        return {...result, ...forTurn};
      }, {});
    // Flag a "search" call as a retry only when a prior call against the same ids
    // actually errored — a repeated search that previously succeeded (e.g. deliberately
    // re-run) is a new search, not a retry.
    const resultStatusByToolUseId = {};
    turns.filter(t => t != null && t.content).forEach(turn => {
      turn.content.filter(b => b?.toolResult).forEach(b => {
        resultStatusByToolUseId[b.toolResult.toolUseId] = b.toolResult.status || "success";
      });
    });
    const lastSearchToolUseIdByKey = new Map();
    Object.values(lookup).forEach(block => {
      const toolUse = block?.toolUse;
      if (toolUse?.name !== "search" || !Array.isArray(toolUse?.input?.ids)) return;
      const key = [...toolUse.input.ids].sort().join(":");
      const priorToolUseId = lastSearchToolUseIdByKey.get(key);
      block.isRetry = !!priorToolUseId && resultStatusByToolUseId[priorToolUseId] === "error";
      lastSearchToolUseIdByKey.set(key, toolUse.toolUseId);
    });
    return lookup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns.length]);

  // Merge sub-agent turns (client.subAgentTurns — one flat map for the whole
  // conversation, live-observable while a sub-agent streams, plain once it
  // finishes) into the lookup. Kept outside useMemo so MobX tracks the map and
  // re-renders on live sub-agent updates.
  const toolUseLookup = toolUseLookupBase;
  if (subAgentTurns) {
    subAgentTurns.forEach((messages, toolUseId) => {
      if (messages && toolUseLookup[toolUseId]) {
        // Set even when empty so ContentBlockInner tracks the array and re-renders on push
        toolUseLookup[toolUseId].agentMessages = messages;
      }
    });
  }

  // stickyTurnIndices: memoized on turn count — sticky assignment only changes when
  // turns are added, not on text deltas.
  const stickyTurnIndices = useMemo(() => {
    const indices = new Set();
    let lastStickyIndex = -1;
    for (let i = 0; i < turns.length; i++) {
      const turn = turns[i];
      if (turn == null) continue;
      if (turn.role === ROLE_USER && !turn.content?.some(block => block.toolResult)) {
        const hasAssistantAfter = turns.slice(i + 1).some(t => t.role === 'assistant');
        if (hasAssistantAfter) lastStickyIndex = i;
      }
    }
    if (lastStickyIndex >= 0) indices.add(lastStickyIndex);
    return indices;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns.length]);

  return (<div ref={conversationRef} className={`conversation${className ? " " + className : ""}`}>
    {welcome}
    {turns.map((turn, turnNumber) => {
      if (turn == null) {
        return null;
      }
      const timestamp = turn.ts && `${formatTimestamp(turn.ts)}` || "";
      const offset = turn.ts && turn.ts !== startTs && ` +${formatTimestamp(turn.ts - startTs)}` || "";
      const isStickyPrompt = stickyTurnIndices.has(turnNumber);
      const editable = editingTurnIndex === turnNumber;
      return (<Turn key={turnNumber}
                    timestamp={showTimestamps && turn.ts && `${timestamp}${offset}` || ""}
                    turn={turn}
                    turnNumber={turnNumber}
                    trapErrors={trapErrors}
                    agentId={agentId}
                    toolUseLookup={toolUseLookup}
                    showAdvancedFeatures={showAdvancedFeatures}
                    showTimestamps={showTimestamps}
                    showDetails={showDetails}
                    resolveSourceLinks={resolveSourceLinks}
                    onVisibilityChange={onVisibilityChange}
                    discardConversation={turnNumber !== turns.length - 1 && discardConversation}
                    replayConversation={replayConversation}
                    busy={busy}
                    userStore={userStore}
                    isStickyPrompt={isStickyPrompt}
                    mentionedGenes={mentionedGenesRef.current}
                    editable={editable}
                    onEdit={() => onEditPrompt && onEditPrompt(turnNumber)}
                    onSave={onSaveEdit}
                    onCancel={onCancelEdit}
      />);
    })}
    {children}
  </div>);
});

export const AgentConversation = inject('searchSessionStore')(observer(({turns, agentId, showAdvancedFeatures = false, showDetails = false, toolName = null, busy = false, replayConversation = null, turnNumber = null, searchSessionStore}) => {
  // Get resolveSourceLinks directly from the store instead of threading through props
  const resolveSourceLinks = searchSessionStore?.resolveSourceLinks;
  const [expanded, setExpanded] = useState(false);
  const [scrollerRef, tailRef, _] = useAutoScroll(false);

  // Track elapsed time while busy for dynamic status indicator (PLEX-5847)
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const startTimeRef = useRef(null);
  useEffect(() => {
    if (busy) {
      if (startTimeRef.current === null) {
        startTimeRef.current = Date.now();
        setElapsedSeconds(0);
      }
      const interval = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      startTimeRef.current = null;
      setElapsedSeconds(0);
    }
  }, [busy]);

  // Build tooltip - show detailed token and cost info only for admin/power users
  let tooltip = undefined;
  if (showDetails) {
    // Calculate total usage and cost for this sub-agent conversation
    const totalUsage = accumulateUsage(turns);
    const {
      inputTokens = 0,
      outputTokens = 0,
      cacheReadInputTokens = 0,
      cacheWriteInputTokens = 0,
      totalTokens = 0
    } = totalUsage;

    // Format elapsed time for display
    const formatElapsed = (seconds) => {
      if (seconds >= 60) {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
      }
      return `${seconds}s`;
    };

    // Only show tooltip if there are actual token usage details
    if (totalTokens > 0) {
      const cost = calculateCost(totalUsage);
      const elapsedTime = formatElapsed(elapsedSeconds);
      tooltip = `Agent operation details
Time elapsed: ${elapsedTime}
Input: ${inputTokens.toLocaleString()} tokens
Output: ${outputTokens.toLocaleString()} tokens
Cache read: ${cacheReadInputTokens.toLocaleString()} tokens
Cache write: ${cacheWriteInputTokens.toLocaleString()} tokens
Total: ${totalTokens.toLocaleString()} tokens
Estimated cost: $${cost.toFixed(2)}`;
    }
  }

  // Notify children when expansion state changes
  const onVisibilityChange = useCallback(() => {
    // This will be passed down to trigger icon injection when conversation expands
  }, []);

  const toggleExpanded = (e) => {
    e.stopPropagation();
    const newExpanded = !expanded;
    setExpanded(newExpanded);

    // Get source references store
    const sourceReferencesStore = searchSessionStore?.sourceReferencesStore;

    if (newExpanded) {
      // Trigger visibility change callback after expansion
      setTimeout(() => {
        onVisibilityChange();
      }, 150); // Small delay to let DOM update
    } else {
      // Remove sources when collapsing
      if (sourceReferencesStore && agentId) {
        sourceReferencesStore.removeSourcesByAgentId(agentId);
      }
    }
  };

  // Derive high-level status from what the analyst is actively doing
  const progressTurn = turns?.find(t => t?.role === ROLE_SYSTEM && t?._progress != null);
  const activeSearchStage = progressTurn?._progress;
  const progressInfoText = progressTurn?._lastInfo || null;
  const lastPartialTurn = turns?.length
    ? [...turns].reverse().find(t => t?.partial && t.role !== ROLE_SYSTEM) ?? null
    : null;
  const analystHasToolUse = lastPartialTurn?.content?.some(b => b.toolUse);
  const analystHasText = lastPartialTurn?.content?.some(b => b.text?.trim());

  // Count total tool calls across all analyst turns (for activity dot display)
  const totalToolCalls = turns
    ? turns.filter(t => t != null).reduce((n, t) => n + (t.content?.filter(b => b.toolUse).length || 0), 0)
    : 0;

  const isSearch = toolName === "search";
  const toolSuffix = agentId ? ` (${agentId.slice(-4)})` : "";

  // Build elapsed time label for long-running operations (PLEX-5847)
  const elapsedLabel = busy && elapsedSeconds >= 5
    ? elapsedSeconds >= 60
      ? ` ${Math.floor(elapsedSeconds / 60)}m${elapsedSeconds % 60 > 0 ? `${elapsedSeconds % 60}s` : ''}`
      : ` ${elapsedSeconds}s`
    : "";

  // Determine active tool name from the last partial assistant turn (PLEX-5847)
  const activeToolName = (() => {
    if (!busy) return null;
    for (let i = (turns?.length || 0) - 1; i >= 0; i--) {
      const t = turns[i];
      if (t?.partial && t.content) {
        const toolUseBlock = t.content.find(b => b.toolUse);
        if (toolUseBlock?.toolUse?.name) return toolUseBlock.toolUse.name;
      }
    }
    return null;
  })();
  const activeToolLabel = activeToolName ? ` · ${activeToolName}` : "";

  let statusText;
  if (!busy) {
    statusText = isSearch ? "Search Analysis" : "Agent operation complete";
  } else if (activeSearchStage != null && activeSearchStage < 8) {
    statusText = `Running search…${toolSuffix}${elapsedLabel}`;
  } else if (analystHasToolUse) {
    statusText = `Investigating…${toolSuffix}${elapsedLabel}`;
  } else if (analystHasText) {
    statusText = `Analyzing…${toolSuffix}${elapsedLabel}`;
  } else {
    statusText = `using tools${activeToolLabel}${toolSuffix}${elapsedLabel}`;
  }

  // Show spinner only when busy and no live text or tool activity
  const showSpinner = busy && !progressInfoText && !analystHasText && totalToolCalls === 0;

  // Lazy-mount: don't render the nested Conversation until the user has expanded
  // at least once. Without auto-expand-on-complete, most sub-agent conversations
  // stay collapsed for the life of the session — a long run with many tool calls
  // (e.g. 194 in one reported freeze) would otherwise mount a full nested
  // Conversation/Turn/ContentBlock tree per tool call, all hidden via CSS only,
  // still subject to MobX re-renders on every underlying turn mutation.
  // Once expanded, keep it mounted (don't unmount on re-collapse) so the
  // typewriter state and scroll position survive toggling.
  const hasBeenExpandedRef = useRef(expanded);
  if (expanded) hasBeenExpandedRef.current = true;
  const conversation = hasBeenExpandedRef.current && (
    <Conversation key="conversation"
                  className="agent"
                  turns={turns}
                  agentId={agentId}
                  showTimestamps={false}
                  showAdvancedFeatures={showAdvancedFeatures}
                  showDetails={showDetails}
                  resolveSourceLinks={resolveSourceLinks}
                  onVisibilityChange={onVisibilityChange}
    />
  );

  const replayButton = replayConversation && !busy && turnNumber !== null && (
    <div
      className="glyphicon glyphicon-refresh flat-button"
      title="Regenerate the conversation from this point"
      onClick={e => {
        e.stopPropagation();
        replayConversation(turnNumber);
      }}
    ></div>
  );

  return (
    <div className={`agent-conversation-container${expanded ? ' expanded' : ''}${busy ? ' busy' : ' complete'}`} title={tooltip}>
      <div className="agent-status-line">
        <span
          className={`expand-toggle glyphicon glyphicon-triangle-${expanded ? 'bottom' : 'right'}`}
          title={expanded ? "Hide details" : "Show details"}
          onClick={toggleExpanded}
        ></span>
        {showSpinner && <span className="agent-status-spinner spinner" />}
        <span className="status-text" onClick={toggleExpanded}>{progressInfoText || statusText}</span>
        {replayButton}
      </div>
      <div key="scroller" ref={scrollerRef}
           className={`conversation-scroller conversation-scroller-agent tool-result${expanded ? '' : ' agent-scroller-hidden'}`}>
        {busy && !showAdvancedFeatures && totalToolCalls > 0 && (
          <div className="agent-tool-activity">
            {"● ".repeat(totalToolCalls).trim()}
          </div>
        )}
        {conversation}
        <div key="convo-tail" ref={tailRef} className={"convo-tail"}></div>
      </div>
      {expanded && (
        <div className="agent-collapse-footer" onClick={toggleExpanded}>
          <span className="collapse-toggle glyphicon glyphicon-triangle-top" title="Hide details"></span>
          <span className="collapse-text">Hide details</span>
        </div>
      )}
    </div>
  );
}));
